import { Fragment, ReactNode } from 'react';
import { Disclosure, Tab, Transition } from '@headlessui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconLookup } from '@fortawesome/fontawesome-svg-core';
import { faBook, faCircleInfo, faMinus, faPlus, faStar } from '@fortawesome/pro-regular-svg-icons';
import { ExpandedProduct } from '@v2/shared/lib/types';
import ProductReviewsPanel from '@v2/shared/components/ProductReviewsPanel';
import Authors from './Authors';
import normalizeGenomeAttributes from './genomeAttributes';

const TAB_CLASSES =
  'text-lg px-4 py-2 bg-white font-semibold border-b-slate-200 ui-selected:text-red-600 border-b-2 ui-selected:border-b-red-600 text-slate-800 hover:border-slate-400 -mb-[2px]';

type Props = {
  product: ExpandedProduct;
};

/**
 * Description and Details, as tabs on desktop and as an accordion on the phone — the
 * same two panels either way, so they are written once and mounted twice.
 *
 * Reviews is a tab group of its own rather than a third tab, and always below the other
 * two. It is a full-width third-party widget and the anchor the star rating scrolls to,
 * neither of which works inside a panel that might not be selected.
 */
export default function ProductTabs({ product }: Props) {
  const showDetails = Object.keys(product.genome_attributes ?? {}).length > 0;
  const description = <DescriptionPanel product={product} />;
  const details = <DetailsPanel product={product} />;

  return (
    <>
      <div className="hidden md:block">
        <Tab.Group>
          <Tab.List className="flex border-b-2 gap-3 border-slate-200">
            <Tab className={TAB_CLASSES}>
              <FontAwesomeIcon icon={faBook} fixedWidth className="mr-2" />
              Description
            </Tab>
            {showDetails && (
              <Tab className={TAB_CLASSES}>
                <FontAwesomeIcon icon={faCircleInfo} fixedWidth className="mr-2" />
                Details
              </Tab>
            )}
          </Tab.List>
          <Tab.Panels>
            <Tab.Panel>{description}</Tab.Panel>
            {showDetails && <Tab.Panel>{details}</Tab.Panel>}
          </Tab.Panels>
        </Tab.Group>
      </div>

      <div className="mb-16 md:hidden">
        <Accordion defaultOpen icon={faBook} title="Description">
          {description}
        </Accordion>
        {showDetails && (
          <Accordion icon={faCircleInfo} title="Details">
            {details}
          </Accordion>
        )}
      </div>

      {(product.reviews_data?.count ?? 0) > 0 && (
        <Tab.Group>
          <Tab.List className="flex border-b-2 gap-3 border-slate-200">
            <Tab className={TAB_CLASSES}>
              <FontAwesomeIcon icon={faStar} fixedWidth className="mr-2" />
              Reviews
            </Tab>
          </Tab.List>
          <Tab.Panels>
            <Tab.Panel>
              <ProductReviewsPanel product={product} />
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
      )}
    </>
  );
}

function Panel({ children }: { children: ReactNode }) {
  return <div className="pt-3 pb-10 md:pt-4 md:pb-14 lg:pt-5 lg:pb-20">{children}</div>;
}

/**
 * The publisher's copy, as HTML.
 *
 * `product-description` rather than Tailwind Typography's `prose`. That was decided when
 * this was the only server-supplied HTML in the tree and the plugin would have been a
 * dependency taken on for one panel; the plugin is here now (see tailwind.config.js), so
 * the reason has gone and this panel can move whenever someone is in here anyway. It has
 * not been moved as part of an unrelated change. See v2.css, where the rules live.
 *
 * The name is the fallback for a product with no description at all, which is what
 * headless showed and is better than an empty panel under a tab that says Description.
 */
function DescriptionPanel({ product }: { product: ExpandedProduct }) {
  return (
    <Panel>
      <article
        className="product-description max-w-6xl"
        dangerouslySetInnerHTML={{ __html: product.description || product.name }}
      />
    </Panel>
  );
}

/** Publisher, publication date, page count, isbn — whatever the genome carries. */
function DetailsPanel({ product }: { product: ExpandedProduct }) {
  const attributes = normalizeGenomeAttributes(product);

  return (
    <Panel>
      {attributes.map((attribute) => (
        <div key={attribute.label} className="grid grid-cols-2 md:grid-cols-8 pb-4 md:pb-2">
          <span className="col-span-2 md:col-span-2 font-semibold inline-block mr-5 w-56">
            {attribute.label}:
          </span>
          <span className="col-span-2 md:col-span-6 font-normal">
            {attribute.authors ? <Authors authors={attribute.authors} /> : attribute.value}
          </span>
        </div>
      ))}
    </Panel>
  );
}

function Accordion({
  defaultOpen,
  icon,
  title,
  children,
}: {
  defaultOpen?: boolean;
  icon: IconLookup;
  title: string;
  children: ReactNode;
}) {
  return (
    <Disclosure defaultOpen={defaultOpen}>
      {({ open }) => (
        <>
          <Disclosure.Button className="border-b-2 border-slate-200 w-full bg-white py-3 font-semibold text-left text-xl flex justify-between items-center first-of-type:border-t-2">
            <span>
              <FontAwesomeIcon icon={icon} fixedWidth className="mr-2" />
              {title}
            </span>
            <FontAwesomeIcon icon={open ? faMinus : faPlus} className="mr-3" />
          </Disclosure.Button>
          <Transition
            as={Fragment}
            enter="transition-all duration-400 ease-in"
            enterFrom="transform max-h-0 opacity-0"
            enterTo="transform max-h-[5000px] opacity-100"
            leave="transition-all duration-400 ease-out"
            leaveFrom="transform max-h-[5000px] opacity-100"
            leaveTo="transform max-h-0 opacity-0"
          >
            <Disclosure.Panel className="py-3 last-of-type:border-b-[1.5px] border-slate-200 overflow-hidden">
              {children}
            </Disclosure.Panel>
          </Transition>
        </>
      )}
    </Disclosure>
  );
}
